import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import {
  Category as CategoryIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import DeliveryCenterCostsService from "../../../api/DeliveryCenter/deliveryCenterCosts";

const ACCENT = "#059669";
const ACCENT_LIGHT = "#10b981";
const ACCENT_DARK = "#047857";
const ACCENT_BG = "#064e3b";

const CategoryModal = ({ isOpen, onClose, editMode, initialData, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({ ...initialData });
    } else if (isOpen && !editMode) {
      setFormData({ name: "", description: "" });
    }
    if (isOpen) {
      setErrors({});
    }
  }, [initialData, isOpen, editMode]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Nome da categoria e obrigatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      if (editMode) {
        await DeliveryCenterCostsService.updateCostCategory(
          formData.id,
          formData
        );
      } else {
        await DeliveryCenterCostsService.createCostCategory(formData);
      }

      onSuccess(
        editMode
          ? "Categoria atualizada com sucesso!"
          : "Categoria criada com sucesso!"
      );
      onClose();
    } catch (err) {
      console.error("Erro ao salvar categoria:", err);
      setErrors({
        submit: "Falha ao salvar. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&:hover fieldset": { borderColor: alpha(ACCENT, 0.4) },
      "&.Mui-focused fieldset": { borderColor: ACCENT },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.95, y: 10 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 10 },
        transition: { duration: 0.25 },
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${ACCENT_BG} 0%, ${ACCENT_DARK} 50%, ${ACCENT} 100%)`,
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha("#fff", 0.15),
              backdropFilter: "blur(8px)",
            }}
          >
            <CategoryIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                lineHeight: 1.3,
                color: "#ffffff",
              }}
            >
              {editMode ? "Editar Categoria" : "Nova Categoria"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: alpha("#fff", 0.55), fontSize: "0.68rem" }}
            >
              {editMode
                ? "Atualize os dados da categoria"
                : "Cadastre uma nova categoria de custo"}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 0,
            p: 0.75,
            color: alpha("#fff", 0.6),
            borderRadius: 1.5,
            "&:hover": {
              backgroundColor: alpha("#fff", 0.1),
              color: "#fff",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {errors.submit && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: 2 }}
            onClose={() => setErrors((prev) => ({ ...prev, submit: undefined }))}
          >
            {errors.submit}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome da Categoria"
              required
              value={formData.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              variant="outlined"
              size="small"
              error={!!errors.name}
              helperText={errors.name}
              disabled={loading}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descricao"
              multiline
              rows={3}
              value={formData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              variant="outlined"
              size="small"
              disabled={loading}
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.grey[50], 0.5),
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: theme.palette.text.secondary,
            borderRadius: 2,
          }}
        >
          Cancelar
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          startIcon={<SaveIcon />}
          loadingPosition="start"
          disabled={!formData.name}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
            boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
            "&:hover": {
              boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
            },
          }}
        >
          {editMode ? "Atualizar" : "Criar"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryModal;
